import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ClickAwayListener, Fade, Paper, Popper } from '@mui/material';
import PropTypes from 'prop-types';
import MealAdvancedSearchCriteria from './meal_advanced_search_criteria';
import MealAdvancedSearchList from './meal_advanced_search_list';
import MealAdvancedSearchTopResultsPage from './meal_advanced_search_top_results_page';
import PopupTitle from '../common/popup_title';
import {
  GRC_RESULTS_GROUP_LABEL,
  useMealSearch,
} from '../common/meal_search_hook';
import { allIngredientNames } from '../../services/ingredients';
import { userLocale } from '../../services/users';
import {
  useGetDistilledMealsByParentsQuery,
  useGetProgrammesQuery,
  useLazyGetFullMealQuery,
  useLazyGetGRCRecipesByIDsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const OWN_MEALS_GROUP_LABEL = 'Own meals';

const MealAdvancedSearchPopover = ({
  section,
  anchorEl,
  memoizedRecipesBoardIDs,
  initialSearchString,
  grcEnabled,
  onGRCMealSelected,
  onOwnMealSelected,
  onDismiss,
}) => {
  const { data: userProfile } = useGetUserProfileQuery();

  const recentlyUsedSearchCriteria =
    userProfile?.recentlyUsedSearchCriteria || {};

  const userDefaultLocale = userLocale(userProfile);

  const [searchCriteria, setSearchCriteria] = useState({
    ...(recentlyUsedSearchCriteria || {}),
    searchString:
      initialSearchString || recentlyUsedSearchCriteria?.searchString,
    locales: recentlyUsedSearchCriteria?.locales || [userDefaultLocale],
  });

  const [route, setRoute] = useState({ page: 'advancedCriteria' });

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const memoizedParentTitles = useMemo(() => {
    const parentTitles = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const recipesBoard of ownedRecipesBoards || []) {
      parentTitles[recipesBoard.id] = recipesBoard.title;
    }
    return parentTitles;
  }, [ownedRecipesBoards]);

  const groupsConfig = useMemo(() => {
    const cfg = [];
    if (memoizedRecipesBoardIDs.length > 0) {
      cfg.push({
        groupLabel: OWN_MEALS_GROUP_LABEL,
        parentIDs: memoizedRecipesBoardIDs,
      });
    }
    if (grcEnabled) {
      cfg.push({ groupLabel: GRC_RESULTS_GROUP_LABEL });
    }
    return cfg;
  }, [grcEnabled, memoizedRecipesBoardIDs]);

  const [results, estimatedCount, isLoading, onLoadMore, ingredientTreeNodes] =
    useMealSearch(
      searchCriteria,
      groupsConfig,
      section,
      searchCriteria.locales,
      false,
    );

  const loadedResultsCount = results
    ? results.reduce((acc, group) => acc + group.data.length, 0)
    : 0;

  const moreAvailable = results.some(
    (groupResults) => groupResults.moreAvailable,
  );

  const ownMealResults = results.find(
    (groupResults) => groupResults.groupLabel === OWN_MEALS_GROUP_LABEL,
  );
  const smorgMealResults = results.find(
    (groupResults) => groupResults.groupLabel === GRC_RESULTS_GROUP_LABEL,
  );
  const ownMealsLoading = ownMealResults?.loading;
  const smorgMealsLoading = smorgMealResults?.loading;

  const paperRef = useRef();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetGRCRecipesByIDs] = useLazyGetGRCRecipesByIDsQuery();

  const getRecipeAndParent = useCallback(
    (itemID) => {
      /**
       * uniqueMealID is either a meal ID or a GRC recipe ID.
       * Returns a promise which returns an object with the following structure:
       * recipe: a Recipe (not a Meal)
       * parentTitle: title of the recipes board
       * parentLink: router link to the recipes board
       */
      const getRecipeAndParentOperation = (
        uniqueMealID,
        meals,
        grcRecipes,
        boardsTitles,
      ) => {
        console.log(
          `getRecipeAndParentOperation ${uniqueMealID} in ${meals.length} meals and ${grcRecipes.length} GRC recipes`,
        );
        const grcResult = grcRecipes.find(
          (result) => result.grcRecipeID === uniqueMealID,
        );
        if (grcResult) {
          return lazyGetGRCRecipesByIDs([uniqueMealID]).then(
            ({ data: grcRecipesByIDs }) => ({
              recipe: grcRecipesByIDs[uniqueMealID].recipe,
            }),
          );
        }
        const ownMealResult = meals.find(
          (result) => result.mealID === uniqueMealID,
        );
        if (ownMealResult) {
          return lazyGetMeal(uniqueMealID).then(({ data: fullMeal }) => {
            console.log(`Meal parent ${fullMeal.smorgBoardID}`);
            const recipesBoardTitle = boardsTitles[fullMeal.smorgBoardID];
            const parentTitle = recipesBoardTitle || null;
            const parentLink =
              recipesBoardTitle && `/recipes/${fullMeal.smorgBoardID}`;
            console.log({ parentLink, parentTitle });
            return { recipe: fullMeal.recipes[0], parentLink, parentTitle };
          });
        }
        return Promise.resolve({ recipe: null });
      };

      return getRecipeAndParentOperation(
        itemID,
        ownMealResults?.data || [],
        smorgMealResults?.data || [],
        memoizedParentTitles,
      );
    },
    [
      ownMealResults?.data,
      smorgMealResults?.data,
      memoizedParentTitles,
      lazyGetGRCRecipesByIDs,
      lazyGetMeal,
    ],
  );

  const onLoadMoreOwnMeals = () =>
    onLoadMore(OWN_MEALS_GROUP_LABEL, ownMealResults?.nextOffset);

  const onLoadMoreSmorgMeals = () =>
    onLoadMore(GRC_RESULTS_GROUP_LABEL, smorgMealResults?.nextOffset);

  const { data: ownedProgrammes } = useGetProgrammesQuery(undefined);

  const allProgrammesAssociatedRecipesBoardIDs = (
    ownedProgrammes || []
  ).flatMap((p) => p.recipesBoardIDs || []);

  const { data: distilledMealsByParents } = useGetDistilledMealsByParentsQuery(
    allProgrammesAssociatedRecipesBoardIDs,
    {
      skip: allProgrammesAssociatedRecipesBoardIDs.length === 0,
    },
  );

  const allTags = Object.values(distilledMealsByParents || {}).flatMap(
    (meals) => Object.values(meals).flatMap((m) => m.recipes[0]?.tags || []),
  );

  return (
    <Popper
      anchorEl={anchorEl}
      open
      placement="right"
      onClose={onDismiss}
      // Keep the popper above the Fab
      sx={{ zIndex: 'modal' }}
      transition>
      {({ TransitionProps }) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Fade {...TransitionProps} timeout={350}>
          <Paper
            ref={paperRef}
            elevation={3}
            className="light-background"
            style={{
              width: '420px',
              outline: 'none',
              paddingBottom: 10,
            }}>
            <ClickAwayListener
              onClickAway={() => {
                /* do nothing */
              }}>
              <>
                {route.page === 'advancedCriteria' && (
                  <>
                    <PopupTitle
                      titleText="Advanced search"
                      closeButtonEnabled
                      onClickClose={onDismiss}
                    />
                    <MealAdvancedSearchCriteria
                      searchCriteria={searchCriteria}
                      onChangeSearchCriteria={setSearchCriteria}
                      totalResultsLoading={isLoading}
                      totalResults={estimatedCount}
                      loadedResultsCount={loadedResultsCount}
                      moreAvailable={moreAvailable}
                      onShowResults={() => setRoute({ page: 'topResults' })}
                      onDismiss={onDismiss}
                      allIngredients={allIngredientNames(ingredientTreeNodes)}
                      allTags={allTags}
                      localeFilterEnabled
                    />
                  </>
                )}
                {route.page === 'topResults' && (
                  <MealAdvancedSearchTopResultsPage
                    onGRCMealSelected={onGRCMealSelected}
                    onOwnMealSelected={onOwnMealSelected}
                    ownMealResults={ownMealResults}
                    ownMealsLoading={ownMealsLoading}
                    smorgMealResults={smorgMealResults}
                    smorgMealsLoading={smorgMealsLoading}
                    onChangeSearchCriteria={setSearchCriteria}
                    onGoToMyMeals={() => setRoute({ page: 'myMeals' })}
                    onGoToSmorgMeals={() => setRoute({ page: 'smorgMeals' })}
                    onBack={() => setRoute({ page: 'advancedCriteria' })}
                    getRecipeAndParent={getRecipeAndParent}
                    parentTitles={memoizedParentTitles}
                    onDismiss={onDismiss}
                  />
                )}
                {route.page === 'myMeals' && (
                  <MealAdvancedSearchList
                    groupID={route.groupID}
                    mealResults={[
                      {
                        groupID: null,
                        groupLabel: null,
                        results: ownMealResults,
                      },
                    ]}
                    mealsLoading={ownMealsLoading}
                    onMealSelected={onOwnMealSelected}
                    onLoadMore={onLoadMoreOwnMeals}
                    onBack={() => {
                      setRoute({ page: 'topResults' });
                    }}
                    onGoToGroup={(groupID) =>
                      setRoute({ page: 'myMeals', groupID })
                    }
                    parentTitles={memoizedParentTitles}
                    getRecipeAndParent={getRecipeAndParent}
                    onDismiss={onDismiss}
                  />
                )}
                {route.page === 'smorgMeals' && (
                  <MealAdvancedSearchList
                    mealResults={[
                      {
                        groupID: null,
                        groupLabel: null,
                        results: smorgMealResults,
                      },
                    ]}
                    mealsLoading={smorgMealsLoading}
                    onMealSelected={onGRCMealSelected}
                    onLoadMore={onLoadMoreSmorgMeals}
                    onBack={() => {
                      setRoute({ page: 'topResults' });
                    }}
                    getRecipeAndParent={getRecipeAndParent}
                    onDismiss={onDismiss}
                  />
                )}
              </>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

MealAdvancedSearchPopover.propTypes = {
  section: PropTypes.string.isRequired,
  anchorEl: PropTypes.element.isRequired,
  memoizedRecipesBoardIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialSearchString: PropTypes.string,
  grcEnabled: PropTypes.bool,
  onGRCMealSelected: PropTypes.func,
  onOwnMealSelected: PropTypes.func.isRequired,
  onGoToQuickSearch: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

MealAdvancedSearchPopover.defaultProps = {
  grcEnabled: false,
  initialSearchString: null,
  onGRCMealSelected: () => {},
};

export default MealAdvancedSearchPopover;
